import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link to="/" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 tracking-tight whitespace-nowrap">
              YOYBUY<span className="text-accent-500">.PRO</span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors whitespace-nowrap cursor-pointer">
                {t('nav.home')}
              </Link>
              <a href="#categories" className="text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors whitespace-nowrap">
                {t('nav.categories')}
              </a>
              <Link to="/article/yoybuy-spreadsheet-guide" className="text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors whitespace-nowrap cursor-pointer">
                {t('nav.shopping_guide')}
              </Link>
              <a href="#guides" className="text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors whitespace-nowrap">
                {t('nav.guides')}
              </a>
              <a href="#contact" className="text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors whitespace-nowrap">
                {t('nav.contact')}
              </a>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <span>{t('hero.cta_buy')}</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </a>

              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
                aria-label={mobileOpen ? t('mobile.close') : t('mobile.menu')}
              >
                {mobileOpen ? (
                  <i className="ri-close-line text-2xl"></i>
                ) : (
                  <i className="ri-menu-line text-2xl"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm" onClick={closeMobile}></div>
          <div className="absolute top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-background-50 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 h-16 border-b border-background-200">
              <span className="text-lg font-heading font-bold text-foreground-900">
                YOYBUY<span className="text-accent-500">.PRO</span>
              </span>
              <button onClick={closeMobile} className="w-10 h-10 flex items-center justify-center text-foreground-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 px-5">
              <div className="space-y-1">
                <Link
                  to="/"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-home-4-line text-lg text-foreground-600 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">{t('nav.home')}</span>
                </Link>

                <div className="pt-3 pb-2">
                  <span className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                    {t('nav.categories')}
                  </span>
                </div>

                {[
                  { name: 'Shoes', icon: 'ri-footprint-line', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
                  { name: 'Hoodies / Sweaters', icon: 'ri-t-shirt-line', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
                  { name: 'T-Shirts', icon: 'ri-t-shirt-air-line', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
                  { name: 'Jackets', icon: 'ri-vip-crown-line', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
                  { name: 'Pants / Shorts', icon: 'ri-pantone-line', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
                  { name: 'Headwear', icon: 'ri-glasses-line', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
                  { name: 'Sets', icon: 'ri-stack-line', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
                  { name: 'Underwear', icon: 'ri-shirt-line', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
                  { name: 'Jersey', icon: 'ri-team-line', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
                  { name: 'Accessories', icon: 'ri-gift-line', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
                  { name: 'Bags', icon: 'ri-shopping-bag-line', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
                  { name: 'Belts', icon: 'ri-archive-line', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
                  { name: 'Perfumes', icon: 'ri-contrast-drop-line', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
                  { name: 'Other Stuff', icon: 'ri-more-line', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
                ].map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-7 h-7 flex items-center justify-center text-accent-500">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <span className="text-sm text-foreground-700 whitespace-nowrap truncate">{cat.name}</span>
                  </a>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-background-200 space-y-1">
                <div className="pb-2">
                  <span className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                    {t('nav.guides')}
                  </span>
                </div>
                {[
                  { label: t('articles.guide.title'), slug: '/article/yoybuy-spreadsheet-guide' },
                  { label: t('articles.vs_kakobuy.title'), slug: '/article/yoybuy-vs-kakobuy' },
                  { label: t('articles.vs_superbuy.title'), slug: '/article/superbuy-vs-yoybuy' },
                  { label: t('articles.taobao_guide.title'), slug: '/article/taobao-spreadsheet-guide' },
                  { label: t('articles.shipping.title'), slug: '/article/china-shipping-guide' },
                  { label: t('articles.safety.title'), slug: '/article/safe-china-buying' },
                ].map((item) => (
                  <Link
                    key={item.slug}
                    to={item.slug}
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-article-line text-lg text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                    <span className="text-sm text-foreground-700 line-clamp-1">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="p-5 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <span>{t('hero.cta_buy')}</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}